import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, fmt, fmt2 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Calendar, Eye, Edit2 } from "lucide-react";
import { toast } from "sonner";
import PriceBreakdown from "@/components/PriceBreakdown";

const CATS = ["adventure", "explore", "medical", "relaxation", "fun", "historic", "cultural", "spiritual"];
const blank = { title: "", description: "", category: "adventure", base_price: 100, location: "", duration_hours: 2, inclusions: "", images: "", highlights: "", capacity_per_slot: 10, cancel_window_hours: 24, cancel_policy_text: "Free cancellation up to 24 hours before the trip.", featured: false };

export default function VendorListings() {
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(blank);
  const [savingAv, setSavingAv] = useState(null);
  const [avDate, setAvDate] = useState("");
  const [avSlots, setAvSlots] = useState(10);

  const load = async () => {
    try {
      const r = await api.get("/vendor/listings");
      setListings(r.data);
    } catch (error) {
      console.error("Failed to load listings:", error);
      toast.error("Failed to load listings. Please try again.");
    }
  };
  
  useEffect(() => {
    load();
  }, []);

  const startCreate = () => { setEditing(null); setF(blank); setOpen(true); };
  const startEdit = (item) => {
    setEditing(item);
    setF({ ...item, inclusions: (item.inclusions || []).join("\n"), highlights: (item.highlights || []).join("\n"), images: (item.images || []).join("\n") });
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      title: f.title, description: f.description, category: f.category,
      base_price: Number(f.base_price), location: f.location, duration_hours: Number(f.duration_hours),
      inclusions: (f.inclusions || "").split("\n").map((s) => s.trim()).filter(Boolean),
      highlights: (f.highlights || "").split("\n").map((s) => s.trim()).filter(Boolean),
      images: (f.images || "").split("\n").map((s) => s.trim()).filter(Boolean),
      capacity_per_slot: Number(f.capacity_per_slot) || 10,
      cancel_window_hours: Number(f.cancel_window_hours) || 24,
      cancel_policy_text: f.cancel_policy_text || "",
      featured: !!f.featured,
    };
    try {
      if (editing) { await api.put(`/vendor/listings/${editing.listing_id}`, payload); toast.success("Listing updated"); }
      else { await api.post("/vendor/listings", payload); toast.success("Listing created"); }
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Archive this listing?")) return;
    try { await api.delete(`/vendor/listings/${id}`); toast.success("Archived"); load(); } catch (e) { toast.error("Could not archive"); }
  };

  const addAvailability = async (listingId) => {
    if (!avDate) { toast.error("Pick a date"); return; }
    try {
      await api.post(`/vendor/listings/${listingId}/availability`, { date: avDate, slots: Number(avSlots) });
      toast.success("Availability added"); setSavingAv(null); setAvDate(""); setAvSlots(10);
    } catch (e) { toast.error("Could not save availability"); }
  };

  const pricePreview = Number(f.base_price || 0);
  const previewFee = Math.round(pricePreview * 0.10 * 100) / 100;
  const previewFinal = Math.round((pricePreview + previewFee) * 100) / 100;

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-10" data-testid="vendor-listings-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">My listings</h1>
          <p className="text-muted-foreground">Create and manage your experiences.</p>
        </div>
        <Button onClick={startCreate} className="rounded-xl" data-testid="vendor-create-listing-button"><Plus className="h-4 w-4 mr-2" /> New listing</Button>
      </div>

      <div className="mt-6 grid gap-3">
        {listings.length === 0 && <div className="rounded-2xl border border-dashed border-border p-10 text-center" data-testid="vendor-no-listings">
          <p className="font-medium">No listings yet</p>
          <p className="text-sm text-muted-foreground">Create your first offering to start earning.</p>
          <Button onClick={startCreate} className="mt-4 rounded-xl"><Plus className="h-4 w-4 mr-2" /> Create listing</Button>
        </div>}

        {listings.map((l) => (
          <div key={l.listing_id} className="rounded-2xl bg-card border border-border p-4" data-testid="vendor-listing-row">
            <div className="flex flex-col md:flex-row gap-4">
              <img src={(l.images||[])[0] || "https://images.unsplash.com/photo-1542051841857-5f90071e7989?w=800"} alt="" className="h-28 w-full md:w-44 object-cover rounded-xl" />
              <div className="flex-1">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-muted capitalize">{l.category}</span>
                    <h3 className="font-semibold mt-1">{l.title}</h3>
                    <p className="text-xs text-muted-foreground">{l.location} · {l.duration_hours}h · {l.bookings_count || 0} bookings · {l.views || 0} views</p>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-muted-foreground">Vendor pays</div>
                    <div className="text-sm">Base <span className="font-semibold">{fmt2(l.price?.base)}</span></div>
                    <div className="text-xs text-muted-foreground">Customer pays <span className="text-foreground font-semibold">{fmt2(l.price?.final)}</span> (incl. 10% fee)</div>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button variant="ghost" onClick={() => startEdit(l)} className="rounded-xl" data-testid="vendor-edit-listing-button"><Edit2 className="h-4 w-4 mr-2" /> Edit</Button>
                  <Button variant="ghost" onClick={() => remove(l.listing_id)} className="rounded-xl text-destructive" data-testid="vendor-archive-listing-button"><Trash2 className="h-4 w-4 mr-2" /> Archive</Button>
                  <Button variant="secondary" onClick={() => { setSavingAv(l.listing_id); setAvDate(""); setAvSlots(l.capacity_per_slot || 10); }} className="rounded-xl" data-testid="vendor-add-availability-button"><Calendar className="h-4 w-4 mr-2" /> Add availability</Button>
                  <a href={`/listings/${l.listing_id}`} className="inline-flex"><Button variant="ghost" className="rounded-xl"><Eye className="h-4 w-4 mr-2" /> Preview</Button></a>
                </div>
                {savingAv === l.listing_id && (
                  <div className="mt-3 grid sm:grid-cols-3 gap-2 items-end">
                    <label className="text-sm">Date<Input type="date" value={avDate} onChange={(e) => setAvDate(e.target.value)} className="mt-1" data-testid="availability-date-input" /></label>
                    <label className="text-sm">Slots<Input type="number" min={1} value={avSlots} onChange={(e) => setAvSlots(e.target.value)} className="mt-1" data-testid="availability-slots-input" /></label>
                    <Button onClick={() => addAvailability(l.listing_id)} className="rounded-xl" data-testid="availability-save-button">Save availability</Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-popover max-w-3xl">
          <DialogHeader><DialogTitle>{editing ? "Edit listing" : "Create listing"}</DialogTitle></DialogHeader>
          <div className="grid gap-3 max-h-[65vh] overflow-y-auto pr-1">
            <label className="text-sm">Title<Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1" data-testid="listing-form-title" /></label>
            <label className="text-sm">Description<Textarea rows={4} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="mt-1" data-testid="listing-form-description" /></label>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="text-sm">Category
                <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                  <SelectTrigger className="mt-1" data-testid="listing-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover">
                    {CATS.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </label>
              <label className="text-sm">Location<Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} className="mt-1" data-testid="listing-form-location" /></label>
              <label className="text-sm">Base price (USD)<Input type="number" min={1} step="0.01" value={f.base_price} onChange={(e) => setF({ ...f, base_price: e.target.value })} className="mt-1" data-testid="listing-form-price" /><span className="block text-xs text-muted-foreground mt-1" data-testid="listing-form-price-hint">Your earnings. Customers pay this + 10% marketplace fee.</span></label>
              <label className="text-sm">Duration (hours)<Input type="number" min={0.5} step="0.5" value={f.duration_hours} onChange={(e) => setF({ ...f, duration_hours: e.target.value })} className="mt-1" data-testid="listing-form-duration" /></label>
              <label className="text-sm">Capacity per slot<Input type="number" min={1} value={f.capacity_per_slot} onChange={(e) => setF({ ...f, capacity_per_slot: e.target.value })} className="mt-1" data-testid="listing-form-capacity" /></label>
              <label className="text-sm">Free-cancellation window (hours)<Input type="number" min={0} value={f.cancel_window_hours} onChange={(e) => setF({ ...f, cancel_window_hours: e.target.value })} className="mt-1" data-testid="listing-form-cancel-window" /></label>
            </div>
            <label className="text-sm">Cancellation policy (shown to tourist)<Textarea rows={2} value={f.cancel_policy_text} onChange={(e) => setF({ ...f, cancel_policy_text: e.target.value })} className="mt-1" data-testid="listing-form-cancel-policy" /></label>
            <label className="text-sm inline-flex items-center gap-2"><input type="checkbox" checked={!!f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} data-testid="listing-form-featured" /> Featured listing</label>
            <label className="text-sm">Image URLs (one per line)<Textarea rows={3} value={f.images} onChange={(e) => setF({ ...f, images: e.target.value })} className="mt-1" data-testid="listing-form-images" /></label>
            <label className="text-sm">Inclusions (one per line)<Textarea rows={3} value={f.inclusions} onChange={(e) => setF({ ...f, inclusions: e.target.value })} className="mt-1" data-testid="listing-form-inclusions" /></label>
            <label className="text-sm">Highlights (one per line)<Textarea rows={3} value={f.highlights} onChange={(e) => setF({ ...f, highlights: e.target.value })} className="mt-1" data-testid="listing-form-highlights" /></label>
            <div>
              <div className="text-sm font-medium mb-1">Pricing transparency</div>
              <PriceBreakdown base={pricePreview} fee={previewFee} total={previewFinal} testidPrefix="listing-form-price-breakdown" />
              <p className="text-xs text-muted-foreground mt-1">Customers see the total above. You receive the base. A flat 10% marketplace fee is added on top.</p>
            </div>
          </div>
          <DialogFooter><Button onClick={save} disabled={!f.title || !f.description || !f.location} className="rounded-xl" data-testid="listing-form-save-button">{editing ? "Save changes" : "Create listing"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
